import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MagnifyingGlass, X } from '@phosphor-icons/react';
import { Icon } from '../Icon';
import { Illustration } from '../../illustrations';
import { cn } from '../../lib/cn';
import '../../patterns/typeahead.css';

/**
 * The clear button every `.dsk-search` shows once it has a value.
 *
 * The inputs are `type="search"`, which means WebKit draws its own cancel
 * button inside them: a filled grey circle with a white cross, at a size and
 * colour nothing here controls, that ignores the theme and reads as an
 * artefact from another app. `.dsa-search-clear` in app-layer.css suppresses
 * it; this is what replaces it. A plain X, current colour, sized like the
 * leading glyph it mirrors.
 */
export function SearchClear({ onClear, label = 'Clear search' }) {
  return (
    <button type="button" className="dsa-search-clear" aria-label={label} onClick={onClear}>
      <Icon as={X} />
    </button>
  );
}

/**
 * The desktop search box, `.dsk-search`.
 *
 * The MarketShell nav gives it the full 560px because on a buying page search
 * is the front door. The ConsoleShell utility bar gives it 360px via
 * `.dsk-utilitybar__search`, because there the primary create action leads.
 *
 * The structural contract in desktop/system.css is a flex row of exactly:
 * leading glyph, <input>, `.dsk-kbd` chip. Kept literally.
 *
 * The chip is not decoration. When `shortcut` is on, Cmd+K (Ctrl+K away from a
 * Mac) focuses this input from anywhere on the page, and the chip is the only
 * thing that tells anyone so. If the shortcut is off the chip is not rendered,
 * because a hint for a key that does nothing is worse than no hint.
 *
 * `suggestions` / `onSelectSuggestion` are presentational only -- this stays a
 * shell primitive, it does not fetch. Both are optional so `UtilityBar`, the
 * other consumer, can keep passing neither and render exactly as before.
 *
 * `emptyState` ({title, body}) is what an untouched box says. It replaced a
 * "Go to" list of nav destinations, which answered a question nobody asked:
 * someone who clicks SEARCH means to search, and the marketplace / sets /
 * orders links were already in the nav directly above the box. The state also
 * gives the panel a reason to open when there is nothing at all to list, which
 * is a first-time user with no history.
 *
 * DISMISSAL. The list stays open until: a suggestion is picked, a click lands
 * outside this box (same `mousedown`-on-document mechanism the pre-conversion
 * `SearchBar.jsx` used), or a fresh `suggestions` array arrives from the
 * caller (a new search resolving reopens it, even after a dismissal). None of
 * this reaches into the caller's `suggestions` state -- it is purely local
 * "am I currently showing what I was given" UI state, so it stays presentational.
 *
 * @param {{
 *   value?: string,
 *   onChange?: (value: string) => void,
 *   onSubmit?: (value: string) => void,
 *   placeholder?: string,
 *   label?: string,
 *   shortcut?: boolean,
 *   inputRef?: object,
 *   suggestions?: Array<{id: string|number, label: string}>,
 *   emptyState?: {title: string, body?: string},
 *   onSelectSuggestion?: (suggestion: {id: string|number, label: string}) => void,
 *   className?: string
 * }} props
 */
/** One shared empty array, so an unset list prop keeps a stable identity. */
const NONE = [];

/**
 * The artwork the not-yet-searched state draws with.
 *
 * The slug this was waiting for now exists. It used to borrow
 * `nothing-catalogued-yet`, which is a claim about OUR data being empty rather
 * than about the user not having typed; the note above this line asked for
 * `search-not-started` and said this constant would be the only line to change,
 * so it is. `search-matched-nothing` remains the opposite fact — a query RAN
 * and found nothing, and the way out of that one is different words.
 */
const EMPTY_ART = 'search-not-started';

export function SearchBox({
  value,
  onChange,
  onSubmit,
  placeholder = 'Search cards, sets and stores',
  label = 'Search',
  shortcut = true,
  /* The Cmd-K CHIP is on by default; the Cmd-K BINDING is not, because the
     command palette owns that combo app-wide. */
  shortcutFocus = false,
  inputRef,
  /* NOT `= []` inline. A fresh literal each render changes `groups`'s memo
     identity every render, which re-runs the effect below and reopens the
     panel the instant anything dismisses it. */
  suggestions = NONE,
  sections = NONE,
  emptyState,
  onSelectSuggestion,
  onRemoveSuggestion,
  openOnFocus = false,
  className,
}) {
  const ownRef = useRef(null);
  const ref = inputRef || ownRef;
  const formRef = useRef(null);

  /* One list, two ways of being given it. `suggestions` is the flat original
     and stays exactly as it was for any caller still passing it; `sections`
     is the grouped form the nav typeahead needs, because "Recent", "Cards"
     and "Go to" are three different KINDS of answer and a buyer reads them
     faster with the kind named than with twenty rows in one run. */
  const groups = useMemo(
    () =>
      sections.length > 0
        ? sections.filter((section) => section.items && section.items.length > 0)
        : suggestions.length > 0
          ? [{ id: 'suggestions', items: suggestions }]
          : [],
    [sections, suggestions],
  );

  /* THE TWO APIS OPEN DIFFERENTLY, and they have to.

     Flat `suggestions` is the original contract: the caller only ever hands
     over a list it has already decided to show, so being given one IS the
     instruction to open. Kept exactly.

     `sections` is the nav typeahead, which is handed a list at all times —
     recent searches sit there from page load. Opening on being given one
     would drop a panel over every page the moment it mounted, so this half
     opens on focus and on nothing else.

     `emptyState` counts as sectioned mode too, and has to. The test was
     `sections.length > 0` alone, but a user with NO search history now gets an
     EMPTY `sections` from the nav hook — which read as "flat `suggestions`
     mode", i.e. open on sight, and would drop a panel over every page on
     mount. Being handed either shape means nav typeahead. */
  const sectioned = sections.length > 0 || Boolean(emptyState);
  const [dismissed, setDismissed] = useState(sectioned);

  /* The panel opens for an EMPTY box too, which it did not before. A caller
     that supplies `emptyState` is saying "there is a reason to open even with
     nothing to list" — that reason being the state itself. Without this, a
     first-time user (no history, nothing typed) focuses the box and gets no
     panel at all, which is exactly the blank Junaid asked to be filled. */
  const showEmptyState = Boolean(emptyState) && !dismissed;
  const showSuggestions = groups.length > 0 && !dismissed;
  const showPanel = showSuggestions || showEmptyState;

  // A fresh list (a new search resolving, in whatever caller owns the fetch)
  // reopens the panel — but in sectioned mode only once the box has actually
  // been focused since the last dismissal, so a background refresh cannot pop
  // one open over a page nobody is searching on.
  const engaged = useRef(false);
  useEffect(() => {
    if (!sectioned || engaged.current) setDismissed(false);
  }, [groups, sectioned]);

  const handleFocus = useCallback(() => {
    engaged.current = true;
    if (openOnFocus) setDismissed(false);
  }, [openOnFocus]);

  // Click-outside dismissal, same mechanism as the pre-conversion
  // `SearchBar.jsx`'s `handleClickOutside`: a document-level `mousedown`
  // listener, not `click`, gated to a ref around the whole box so a mousedown
  // anywhere inside it (including on a suggestion button) is never treated as
  // "outside". Only attached while the list is actually showing.
  useEffect(() => {
    if (!showPanel) return undefined;

    const handleOutsideMouseDown = (event) => {
      if (formRef.current && !formRef.current.contains(event.target)) {
        setDismissed(true);
      }
    };

    document.addEventListener('mousedown', handleOutsideMouseDown);
    return () => document.removeEventListener('mousedown', handleOutsideMouseDown);
  }, [showPanel]);

  // Mac shows the command glyph, everything else shows Ctrl. Read once: the
  // platform cannot change while the page is open.
  const isMac = useMemo(() => {
    if (typeof navigator === 'undefined') return false;
    return /Mac|iPhone|iPad|iPod/i.test(navigator.platform || navigator.userAgent || '');
  }, []);

  useEffect(() => {
    // The CHIP and the BINDING are separate now. Cmd-K opens the command
    // palette, which is a bigger answer to the same question, so the chip
    // stays (it is still the only thing that advertises the shortcut) while
    // the key itself belongs to the palette. Two handlers for one combo would
    // race, and the loser would be whichever mounted first.
    if (!shortcut || !shortcutFocus) return undefined;

    const onKeyDown = (event) => {
      const combo = isMac ? event.metaKey : event.ctrlKey;
      if (!combo || event.key.toLowerCase() !== 'k') return;
      event.preventDefault();
      if (ref.current) ref.current.focus();
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [shortcut, shortcutFocus, isMac, ref]);

  const handleSelectSuggestion = useCallback(
    (suggestion) => {
      // Dismiss locally, immediately -- don't wait on the caller to notice
      // the selection and update its own `suggestions` state (which, in
      // Marketplace's case, also races a route change).
      setDismissed(true);
      if (onSelectSuggestion) onSelectSuggestion(suggestion);
    },
    [onSelectSuggestion]
  );

  /* Removing is the one row action that must NOT dismiss the panel: you delete
     a remembered search in order to look at the shorter list, and a panel that
     closes on the first X makes clearing three of them three round trips.
     `handleSelectSuggestion`'s `setDismissed(true)` is deliberately absent. */
  const handleRemoveSuggestion = useCallback(
    (suggestion) => {
      if (onRemoveSuggestion) onRemoveSuggestion(suggestion);
    },
    [onRemoveSuggestion]
  );

  // Clearing puts focus back in the box. Someone who clears a query is about
  // to type another one, and a clear that drops focus makes them click twice.
  const handleClear = useCallback(() => {
    if (onChange) onChange('');
    if (ref.current) {
      ref.current.value = '';
      ref.current.focus();
    }
  }, [onChange, ref]);

  const hasValue = Boolean(value);

  const handleSubmit = useCallback(
    (event) => {
      event.preventDefault();
      if (onSubmit) onSubmit(ref.current ? ref.current.value : value);
    },
    [onSubmit, ref, value]
  );

  return (
    <form ref={formRef} className={cn('dsk-search', className)} role="search" onSubmit={handleSubmit}>
      <Icon as={MagnifyingGlass} />
      <input
        ref={ref}
        type="search"
        aria-label={label}
        placeholder={placeholder}
        value={value}
        onChange={onChange ? (event) => onChange(event.target.value) : undefined}
        onFocus={handleFocus}
        onKeyDown={(event) => {
          if (event.key === 'Escape' && showPanel) {
            event.stopPropagation();
            setDismissed(true);
          }
        }}
        readOnly={value !== undefined && !onChange}
      />
      {/* The clear button REPLACES the shortcut chip rather than sitting next
          to it. The chip advertises how to get INTO an empty box; once there
          is a query in it the useful control is the way out, and showing both
          put two 20px affordances in a 16px gap. */}
      {hasValue ? (
        <SearchClear onClear={handleClear} />
      ) : (
        shortcut && (
          <span className="dsk-kbd" aria-hidden="true">
            {isMac ? '⌘' : 'Ctrl'}K
          </span>
        )
      )}
      {showPanel ? (
        /* `.dsa-typeahead` is not decoration: patterns/typeahead.css hangs
           every rule off it so its compound selectors outrank the originals
           still sitting in app-layer.css, which loads last. */
        <div className="dsk-search__suggestions dsa-typeahead">
          {showEmptyState ? (
            <div
              className={cn(
                'dsa-typeahead__empty',
                showSuggestions && 'dsa-typeahead__empty--compact',
              )}
            >
              {/* `xs` (64px) when recent searches share the panel, `sm` (96px)
                  when this is all there is. The panel scrolls at 320px and five
                  history rows run ~160px of that, so the full-size art would
                  push the rows it sits above out of view. */}
              <Illustration name={EMPTY_ART} size={showSuggestions ? 'xs' : 'sm'} />
              <p className="dsa-typeahead__empty-title">{emptyState.title}</p>
              {emptyState.body ? (
                <p className="dsa-typeahead__empty-body">{emptyState.body}</p>
              ) : null}
            </div>
          ) : null}
          {groups.map((group) => (
            <React.Fragment key={group.id}>
              {group.title ? (
                <p className="dsa-typeahead__head" aria-hidden="true">
                  {group.title}
                </p>
              ) : null}
              <ul role="listbox" aria-label={group.title || label}>
                {group.items.map((item) => (
                  <li key={`${group.id}-${item.id}`} role="option" aria-selected="false">
                    {/* The row and its remove control are SIBLINGS inside this
                        wrapper, never nested: a <button> inside a <button> is
                        invalid and browsers resolve it by dropping one of
                        them. The wrapper is what hover is read from, so the X
                        appears when the pointer is anywhere on the row rather
                        than only over the 24px it occupies. */}
                    <span
                      className={cn(
                        'dsa-typeahead__row',
                        item.removable && onRemoveSuggestion && 'dsa-typeahead__row--removable'
                      )}
                    >
                      <button
                        type="button"
                        // `onMouseDown`, not `onClick`: selecting clears this
                        // list and usually navigates away, either of which can
                        // remove this button from the DOM before a `click` on it
                        // would otherwise fire. Mousedown resolves the selection
                        // immediately, ahead of that removal -- and ahead of the
                        // outside-dismissal listener above, which also runs on
                        // `mousedown` and must see this as INSIDE the form, never
                        // as an outside click closing the list out from under it.
                        onMouseDown={() => handleSelectSuggestion(item)}
                      >
                        {item.thumb ? (
                          <img src={item.thumb} alt="" className="dsa-typeahead__thumb" />
                        ) : item.icon ? (
                          <Icon as={item.icon} />
                        ) : null}
                        <span className="dsa-typeahead__label">{item.label}</span>
                        {item.sub ? <span className="dsa-typeahead__sub">{item.sub}</span> : null}
                      </button>
                      {item.removable && onRemoveSuggestion ? (
                        <button
                          type="button"
                          className="dsa-typeahead__remove"
                          // The label names the entry, so a screen reader hears
                          // "Remove charizard from recent searches" rather than
                          // five identical "Remove" buttons.
                          aria-label={`Remove ${item.label} from recent searches`}
                          // preventDefault on mousedown keeps focus in the
                          // input: without it the press blurs the field, and a
                          // blurred field is how this panel closes. The actual
                          // removal is on CLICK, which is the only activation a
                          // keyboard or a screen reader dispatches -- doing the
                          // work on mousedown would put this control out of
                          // reach of both.
                          onMouseDown={(event) => event.preventDefault()}
                          onClick={() => handleRemoveSuggestion(item)}
                        >
                          <Icon as={X} size="sm" />
                        </button>
                      ) : null}
                    </span>
                  </li>
                ))}
              </ul>
            </React.Fragment>
          ))}
        </div>
      ) : null}
    </form>
  );
}

export default SearchBox;
